type LegacyColorContext = {
  id?: unknown;
  name?: unknown;
  type?: unknown;
  componentType?: unknown;
  parentId?: unknown;
};

const BACKGROUND_PROPERTIES = new Set(["background", "backgroundColor"]);
const BORDER_PROPERTIES = new Set([
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderColor",
  "outlineColor",
]);
const TEXT_PROPERTIES = new Set(["color", "fill", "stroke"]);

const PAGE_BACKGROUND_HEXES = new Set([
  "#ffffff",
  "#f4f5f7",
  "#f5f7fa",
  "#f6f7f9",
  "#f6f9fb",
  "#f7faf7",
  "#f7fafc",
  "#f8f9fc",
  "#f8fafc",
  "#f9fafb",
]);
const CARD_SURFACE_HEXES = new Set(["#ffffff", "#fff"]);
const SECONDARY_SURFACE_HEXES = new Set([
  "#f1f5f9",
  "#f3f4f6",
  "#f4f5f7",
  "#f5f7fa",
  "#f6f7f9",
  "#f6f9fb",
  "#f7faf7",
  "#f7fafc",
  "#f8f9fc",
  "#f8fafc",
  "#f9fafb",
]);
const BORDER_HEXES = new Set([
  "#cbd5e1",
  "#d1d5db",
  "#dde1e8",
  "#e2e8f0",
  "#e5e7eb",
]);
const PRIMARY_TEXT_HEXES = new Set([
  "#111827",
  "#182230",
  "#1f2937",
  "#23262f",
  "#344054",
  "#374151",
]);
const MUTED_TEXT_HEXES = new Set([
  "#475467",
  "#64748b",
  "#667085",
  "#676f83",
  "#6b7280",
  "#8b95a7",
  "#98a2b3",
  "#9ca3af",
]);

const HEX_PATTERN = /#[0-9a-fA-F]{3}(?:[0-9a-fA-F]{3})?\b/g;
const GAUGE_NEEDLE_COLOR_TOKEN = "hsl(220 10% 48%)";
const LEGACY_GAUGE_NEEDLE_TOKENS = new Set([
  "hsl(var(--muted))",
  "hsl(var(--muted-foreground) / 0.78)",
  "hsl(var(--border-secondary))",
]);

export function normalizeLegacyCanvasBackgroundColor(value: string): string {
  return PAGE_BACKGROUND_HEXES.has(normalizeHex(value))
    ? "hsl(var(--surface-primary))"
    : value;
}

export function normalizeLegacySemanticStyleColor(
  property: string,
  value: string,
  context: LegacyColorContext = {},
): string {
  if (BACKGROUND_PROPERTIES.has(property)) {
    return replaceLegacyHex(value, (hex) => {
      if (isRootSurface(context) && PAGE_BACKGROUND_HEXES.has(hex)) {
        return "hsl(var(--surface-primary))";
      }
      if (CARD_SURFACE_HEXES.has(hex) && isSurfaceComponent(context.componentType ?? context.type)) {
        return "hsl(var(--card))";
      }
      if (SECONDARY_SURFACE_HEXES.has(hex)) {
        return "hsl(var(--surface-secondary))";
      }
      return undefined;
    });
  }

  if (BORDER_PROPERTIES.has(property)) {
    return replaceLegacyHex(value, (hex) =>
      BORDER_HEXES.has(hex) ? "hsl(var(--border))" : undefined,
    );
  }

  if (TEXT_PROPERTIES.has(property)) {
    return replaceLegacyHex(value, (hex) => {
      if (PRIMARY_TEXT_HEXES.has(hex)) return "hsl(var(--foreground))";
      if (MUTED_TEXT_HEXES.has(hex)) return "hsl(var(--muted-foreground))";
      return undefined;
    });
  }

  return value;
}

export function normalizeLegacyGaugeNeedleColor(value: string): string {
  if (LEGACY_GAUGE_NEEDLE_TOKENS.has(value.trim())) {
    return GAUGE_NEEDLE_COLOR_TOKEN;
  }

  return replaceLegacyHex(value, (hex) => {
    if (PRIMARY_TEXT_HEXES.has(hex) || MUTED_TEXT_HEXES.has(hex)) {
      return GAUGE_NEEDLE_COLOR_TOKEN;
    }
    return undefined;
  });
}

function replaceLegacyHex(
  value: string,
  resolveReplacement: (hex: string) => string | undefined,
): string {
  return value.replace(HEX_PATTERN, (match) => resolveReplacement(normalizeHex(match)) ?? match);
}

function normalizeHex(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!/^#[0-9a-f]{3}$/.test(normalized)) return normalized;

  const red = normalized.slice(1, 2);
  const green = normalized.slice(2, 3);
  const blue = normalized.slice(3, 4);
  return `#${red}${red}${green}${green}${blue}${blue}`;
}

function isRootSurface(context: LegacyColorContext): boolean {
  const componentType = context.componentType ?? context.type;
  if (context.parentId === undefined || context.parentId === null) {
    return (
      (componentType === "container" || componentType === "flex-container") &&
      isPageLikeRootSurface(context)
    );
  }

  return (
    context.parentId === "root" &&
    (componentType === "container" || componentType === "flex-container") &&
    isPageLikeRootSurface(context)
  );
}

function isPageLikeRootSurface(context: LegacyColorContext): boolean {
  const value = `${String(context.id ?? "")} ${String(context.name ?? "")}`.toLowerCase();
  return (
    value.includes("root") ||
    value.includes("wrapper") ||
    value.includes("canvas") ||
    value.includes("page")
  );
}

function isSurfaceComponent(componentType: unknown): boolean {
  return (
    componentType === "advanced-table" ||
    componentType === "bar-chart" ||
    componentType === "container" ||
    componentType === "flex-container" ||
    componentType === "card" ||
    componentType === "chart" ||
    componentType === "data-card" ||
    componentType === "donut-chart" ||
    componentType === "file-storage" ||
    componentType === "form-container" ||
    componentType === "gauge-chart" ||
    componentType === "level-indicator" ||
    componentType === "line-chart" ||
    componentType === "map-chart" ||
    componentType === "pie-chart" ||
    componentType === "radial-dial" ||
    componentType === "table" ||
    componentType === "thermostat" ||
    componentType === "vertical-gauge-chart"
  );
}
